import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DataTable, Select, Typography, useSelect } from '@chilecompra/react-kit/components'
import { formatAmount } from '../../modules/utils/formatters'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import truncate from '../../modules/utils/limiter'
import generateYearsBetween from '../../modules/utils/years'
import { onGetTradedItemsThunk } from './TradedProducts.actions'
import IconButton from '../../components/Buttons/IconButton'
import Infobar from '../../components/Infobar/Infobar'
import TreemapChart from '../../components/Charts/TreemapChart'
import TradedProductsNoInfo from '../../components/Errors/TradedProductsNoInfo'
import FichaTradedProducts from '../../components/Infographics/FichaTradedProducts.jsx'
import {
  CategoryIcon,
  FamilyIcon,
  GridWrapper,
  Link,
  ProductIcon,
  SegmentIcon,
  Strong,
  Wrapper
} from './TradedProducts.styles'

const NEXT_TYPES_DICTIONARY = {
  categories: 'products',
  families: 'categories',
  initial: 'segments',
  segments: 'families'
}

const TYPE_ICONS_DICTIONARY = {
  categories: <CategoryIcon />,
  families: <FamilyIcon />,
  products: <ProductIcon />,
  segments: <SegmentIcon />
}

/**
 * The TradedProducts' container.
 */
const TradedProducts = () => {
  const dispatch = useDispatch()
  const { endYear, latestUpdate, loading: initialLoading, startYear } = useSelector(state => state.topNavbar)
  const years = generateYearsBetween(endYear, startYear)
  const { idEntity: entityId, name: entityName } = useSelector(state => state.buyerSection)
  const { chartStructure, csvStructure, loading } = useSelector(state => state.tradedProducts)
  const dataTableRef = useRef()
  const [levels, setLevels] = useState([])
  const [fileName, setFileName] = useState(`Rubros_Transados_${years[0].value}`)
  const [showTable, setShowTable] = useState(false)
  const [expandArrow, setExpandArrow] = useState(false)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: years[0].value,
    changeCallback: updatedValue => {
      dataTableRef.current?.setPage(1)
      dispatch(
        onGetTradedItemsThunk({
          entityId,
          type: NEXT_TYPES_DICTIONARY.initial,
          year: updatedValue,
          callback: () => setLevels([])
        })
      )
      setFileName('Rubros_Transados_' + updatedValue)
    }
  })

  const handleShowTable = updatedValue => setShowTable(updatedValue)
  const handleChartLabelFormatter = useCallback(function () {
    return `
      <div style="position: relative; white-space: initial; width: 100%;">
        ${this.point.value >= 8 ? this.point.label : truncate(this.point.label, 45, ' ...')}
        <br />
        <strong>$${formatAmount(this.point.amount)}</strong>
      </div>
    `
  }, [])
  const handleClickMap = useCallback(
    (type, label, categoryId, familyId, productId, segmentId) => {
      if (['categories', 'families', 'segments'].indexOf(type) === -1) {
        return
      }

      dispatch(
        onGetTradedItemsThunk({
          categoryId,
          entityId,
          familyId,
          productId,
          segmentId,
          type: NEXT_TYPES_DICTIONARY[type],
          year: yearValue,
          callback: () =>
            setLevels(currentLevels => [
              ...currentLevels,
              {
                categoryId,
                familyId,
                productId,
                segmentId,
                icon: TYPE_ICONS_DICTIONARY[NEXT_TYPES_DICTIONARY[type]],
                name: label,
                type: NEXT_TYPES_DICTIONARY[type]
              }
            ])
        })
      )
    },
    [entityId, yearValue]
  )
  const handleClickTree = useCallback(
    (item, index) => {
      dispatch(
        onGetTradedItemsThunk({
          entityId,
          categoryId: item.categoryId,
          familyId: item.familyId,
          segmentId: item.segmentId,
          type: item.type,
          year: yearValue,
          callback: () => setLevels(currentCollection => currentCollection.slice(0, index + 1))
        })
      )
    },
    [entityId, yearValue]
  )
  const handleReset = useCallback(() => {
    setLevels([])
    dispatch(onGetTradedItemsThunk({ entityId, type: NEXT_TYPES_DICTIONARY.initial, year: yearValue }))
  }, [entityId, yearValue])

  const handleCaptureImage = () => exportToImage('traded-products-treemap-chart', fileName)
  const handleDownloadFile = () => exportToCsv(csvStructure, fileName)

  useEffect(() => {
    if (!entityId || initialLoading) {
      return
    }

    dispatch(onGetTradedItemsThunk({ entityId, type: NEXT_TYPES_DICTIONARY.initial, year: yearValue }))
  }, [entityId, initialLoading])

  return (
    <Wrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10}>
          <Typography variant="h2" fontSize="22px" fontWeight="bold">
            Rubros y productos más transados
          </Typography>
          <Typography variant="body2" fontSize="16px" lineHeight="20px" margin="16px 0 48px">
            Conoce cuáles son los productos o servicios más transados por <Strong>{name}</Strong> según el año
            seleccionado.
            <br />
            <br />
            La información de montos transados dentro de esta sección corresponde a monto neto, sin impuestos. Recuerda
            que estos montos que se extraen de la orden de compra que emite una entidad en{' '}
            <Link href="https://www.mercadopublico.cl/Home">www.mercadopublico.cl</Link>, corresponden a compromisos de
            los organismos, no a pagos.
          </Typography>
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container alignItems="center" justifyContent="center">
        <GridWrapper container item align="left" md={8} xs={12}>
          <GridWrapper item align="left" md={3} xs={12}>
            <Select
              options={years}
              label="AÑO"
              value={yearValue}
              onChange={handleChangeYear}
              width="125px"
              margin="0 50px 0 0"
            />
          </GridWrapper>
          <GridWrapper item align="left" md={5} xs={12} />
        </GridWrapper>
        <GridWrapper item align="right" md={2} xs={12} margin="6px 0 0">
          <IconButton
            singleIcon
            disabled={!showTable}
            margin="25px 10px 0 0"
            type="toggleChart"
            variant="outlined"
            onClick={() => handleShowTable(false)}
          />
          <IconButton
            singleIcon
            disabled={showTable}
            margin="25px 0 0"
            type="toggleTable"
            variant="outlined"
            onClick={() => handleShowTable(true)}
          />
        </GridWrapper>
      </GridWrapper>
      <GridWrapper container justifyContent="center" margin="8px 0 0">
        {showTable ? (
          <GridWrapper item xs={12} md={10}>
            <DataTable
              headers={[
                { label: 'Nombre Rubro', value: 'label' },
                { label: 'Monto Total', value: 'amount' }
              ]}
              headersCellsProps={[{ enableSort: true }, { enableSort: true }]}
              initialRows={6}
              loading={loading}
              records={chartStructure}
              recordsDecorators={[({ label }) => label, ({ amount }) => `$${formatAmount(amount)}`]}
              ref={dataTableRef}
            />
          </GridWrapper>
        ) : chartStructure.length >= 1 || loading ? (
          <GridWrapper item md={10} xs={12}>
            <TreemapChart
              data={chartStructure}
              id="traded-products-treemap-chart"
              levels={levels}
              rootIcon={<SegmentIcon />}
              rootName={entityName}
              labelFormatter={handleChartLabelFormatter}
              onClickMap={handleClickMap}
              onClickTree={handleClickTree}
              onReset={handleReset}
            />
          </GridWrapper>
        ) : (
          <GridWrapper item xs={12} md={10}>
            <TradedProductsNoInfo />
          </GridWrapper>
        )}
      </GridWrapper>
      <GridWrapper container justifyContent="center">
        <GridWrapper item xs={12} md={10} margin="32px 0 0">
          <Infobar
            disableDownloadChart={showTable}
            disableDownloadCsv={!showTable}
            disableInfographic={showTable}
            expandArrow={expandArrow}
            infographic={<FichaTradedProducts />}
            latestUpdate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onDownloadFile={handleDownloadFile}
            setExpandArrow={setExpandArrow}
          />
        </GridWrapper>
      </GridWrapper>
    </Wrapper>
  )
}

export default TradedProducts
